import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';

/**
 * CompanyNameCell - Nome da empresa linkado para os detalhes dela (Admin Geral), mesmo
 * padrão usado em AuditRow para a coluna "Empresa".
 * @param {Object} props
 * @param {string} props.name
 * @param {number} props.companyId
 */
export function CompanyNameCell({ name, companyId }) {
  return (
    <Link component={RouterLink} to={`/admin/companies/${companyId}`} sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
      {name || `Empresa #${companyId}`}
    </Link>
  );
}

/**
 * CompanyListTable - Tabela genérica de empresas usada pelas seções da tela "Assinaturas"
 * (trials vencendo, trials vencidos, problemas de pagamento, cancelando). Cada seção define
 * suas próprias colunas via `columns`, evitando quatro componentes de tabela quase idênticos.
 * @param {Object} props
 * @param {string} props.title
 * @param {number} props.count
 * @param {Array<Object>} props.rows
 * @param {Array<{key: string, label: string, align?: string, render: (row: Object) => React.ReactNode}>} props.columns
 * @param {string} [props.emptyMessage]
 */
function CompanyListTable({ title, count, rows = [], columns, emptyMessage = 'Nenhuma empresa nesta situação' }) {
  return (
    <Paper
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #E8ECF0',
        overflow: 'hidden',
        mb: 3,
      }}
    >
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #E8ECF0', display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">({count})</Typography>
      </Box>

      <TableContainer sx={{ boxShadow: 'none', borderRadius: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8F9FC' }}>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align || 'left'}
                  sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.company_id} hover>
                  {columns.map((col) => (
                    <TableCell key={col.key} align={col.align || 'left'}>
                      {col.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default CompanyListTable;
